import type { ReactNode } from "react"
import React, { useCallback, useEffect, useState } from "react"
import type { FormDataObject } from "../../../interfaces"

// flatten all form fields across sections
const flattenFormFields = (formData: FormDataObject[]): FormDataObject[] => {
  const allFields: FormDataObject[] = []
  
  formData.forEach((section) => {
    allFields.push(section)
    
    if (section.children && section.children.length > 0) {
      section.children.forEach((field) => {
        allFields.push(field)
      })
    }
  })
  
  return allFields
}

interface IProps {
  item: FormDataObject
  items: FormDataObject[]
  children: ReactNode
  allFormData?: FormDataObject[] 
}

function ConditionalItem(props: IProps) {
  const { item, items, children, allFormData } = props
  
  const searchableItems = allFormData ? flattenFormFields(allFormData) : items
  const hasRules = item.rules && item.rules.length > 0
  const [dependantInputs, setDependantInputs] = useState<string[]>([])

  const renderConditions = useCallback(() => {
    if (item.rules) {
      const inputWithRules = item.rules.map((rule) => {
        const selectedInput = searchableItems.find((i) => i.id === rule.inputId)
        if (selectedInput) {
          if (rule.behavior === "reload") {
            const event = new CustomEvent("reloadData", { detail: { inputId: selectedInput.id } })
            window.dispatchEvent(event)
            return "block"
          }
          if (rule.behavior === "visibility") {
            
            const conditionResult = operator(rule.condition, selectedInput.value, rule.value)
            let displayResult;
            if (rule.action === "visible" && conditionResult) {
              displayResult = "block"
            } else if (rule.action === "hidden" && conditionResult) {
              displayResult = "none"
            } else if (rule.action === "hidden") {
              displayResult = "block"
            } else {
              displayResult = "none"
            }
            
            return displayResult
          }
        } 
        return "block"
      })
      setDependantInputs([...inputWithRules])
    }
  }, [item, items, searchableItems])

  useEffect(() => {
    renderConditions()
  }, [renderConditions])

  const operator = (type: "equal" | "not equal" | "contains" | "not empty" | "greater than or equal to" | "less than or equal to" | "any value", a: any, b: any): boolean => {
    if (a === null || a === undefined) {
      return type === "not empty" ? false : type === "not equal"
    }

    const arrayA = Array.isArray(a) ? a : [a]
    const arrayB = Array.isArray(b) ? b : [b]

    switch (type) {
      case "equal":
        if (Array.isArray(a)) {
          if (!Array.isArray(b)) {
            return a.length === 1 && a[0] === b
          }
          if (a.length !== b.length) return false
          return a.every((val, index) => val === b[index])
        }
        return a === b
        
      case "contains":
        if (Array.isArray(a)) {
          if (!Array.isArray(b)) {
            return a.includes(b)
          }
          return arrayA.some(valueA => 
            arrayB.some(valueB => {
              if (typeof valueA === 'string' && typeof valueB === 'string') {
                return valueA.includes(valueB) || valueA === valueB
              }
              return valueA === valueB
            })
          )
        }
        return typeof a === 'string' && typeof b === 'string' && a.includes(b)
        
      case "not equal":
        if (Array.isArray(a)) {
          if (!Array.isArray(b)) {
            return !(a.length === 1 && a[0] === b)
          }
          if (a.length !== b.length) return true
          return !a.every((val, index) => val === b[index])
        }
        return a !== b
        
      case "not empty":
        if (Array.isArray(a)) {
          return a.length > 0 && a.some(val => val !== null && val !== undefined && val !== "")
        }
        return typeof a !== "undefined" && a !== null && a !== "" && (typeof a !== 'string' || a.length > 0)
        
      case "greater than or equal to":
        const numA = Array.isArray(a) ? parseFloat(a[0]) : parseFloat(a)
        const numB = Array.isArray(b) ? parseFloat(b[0]) : parseFloat(b)
        return !isNaN(numA) && !isNaN(numB) && numA >= numB
        
      case "less than or equal to":
        const numA2 = Array.isArray(a) ? parseFloat(a[0]) : parseFloat(a)
        const numB2 = Array.isArray(b) ? parseFloat(b[0]) : parseFloat(b)
        return !isNaN(numA2) && !isNaN(numB2) && numA2 <= numB2
        
      case "any value":
        if (Array.isArray(a)) {
          return a.length > 0 && a.some(val => val !== null && val !== undefined && val !== "")
        }
        return a !== null && a !== undefined && a !== ""
    }
  }

  const shouldShow = !hasRules || !dependantInputs.includes("none")

  if (shouldShow) {
    return children
  }
  return <div style={{ display: "none" }}>{children}</div>
}

export default ConditionalItem
